<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Ride History Report..."
    no-action
  >
    <template #title>
      Ride History Report
    </template>

    <template #content>
      <template v-if="statistics">
        <div class="row justify-content-center">
          <el-radio-group v-model="period">
            <el-radio-button
              v-for="item in periodList"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <div class="row justify-content-center">
          from {{ formattedFromDate }} to {{ formattedToDate }}
        </div>

        <template
          v-for="group in formattedStatistics"
          :key="group.group"
        >
          <h6 class="sub-section-title">
            {{ group.group }}
          </h6>
          <div
            v-for="(row, index) in group.rows"
            :key="index"
            class="row mb-2"
          >
            <template
              v-for="item in row"
              :key="item.label"
            >
              <label class="col-sm-4 col-6 control-label">
                {{ item.label }}
              </label>
              <div class="col-sm-2 col-6">
                {{ item.value }}
              </div>
            </template>
          </div>
        </template>
      </template>

      <div class="row justify-content-center mt-4">
        <p-button
          v-if="statistics"
          type="purple"
          round
          :loading="status.isLoading"
          @click="() => refreshReport()"
        >
          Refresh Report
        </p-button>
        <p-button
          v-else
          type="purple"
          round
          :loading="status.isLoading"
          @click="() => loadReport()"
        >
          Load Report
        </p-button>
      </div>
    </template>
  </card-container>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch } from 'vue';

import moment from 'moment-timezone';
import { usePartner } from '@/use/partner';
import { fetchPartnerRideHistoryReport, FetchPartnerRideHistoryReportOutput } from '@/services/partners/fetchPartnerRideHistoryReport';

const pageId = 'partner-ride-history-report';

const periodList = [
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Current Month', value: 'currentMonth' },
];

const { partnerCode } = usePartner();

const status = reactive({
  isLoading: false,
});

const statistics = ref<FetchPartnerRideHistoryReportOutput | null>(null);

const period = ref('currentMonth');

const fromDate = computed<string>(() => {
  if (period.value === 'lastMonth') {
    return moment.utc().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  }

  // current month
  return moment.utc().startOf('month').format('YYYY-MM-DD');
});

const toDate = computed<string>(() => {
  if (period.value === 'lastMonth') {
    return moment.utc().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
  }

  // current month
  return moment.utc().endOf('month').format('YYYY-MM-DD');
});

const formattedFromDate = computed<string>(() => moment.utc(fromDate.value).format('MMM Do'));
const formattedToDate = computed<string>(() => moment.utc(toDate.value).format('MMM Do'));

type Statistics = {
  group: string;
  rows: { label: string; value: string | number }[][];
}[];

const formattedStatistics = computed<Statistics | null>(() => {
  if (!statistics.value) {
    return null;
  }

  return [
    {
      group: 'Members',
      rows: [
        [
          { label: 'Active Members', value: statistics.value.activeMembers },
          { label: 'New Members', value: statistics.value.newMembers },
        ],
        [{ label: 'Total Members', value: statistics.value.totalMembers }],
      ],
    },
    {
      group: 'Rides',
      rows: [
        [
          {
            label: 'Number of Completed Rides',
            value: statistics.value.numberOfCompletedRides,
          },
          { label: 'Unique Riders', value: statistics.value.uniqueRiders },
        ],
        [
          {
            label: 'Average Distance',
            value: statistics.value.averageDistance ? `${statistics.value.averageDistance.toFixed(1)} miles` : '-',
          },
          {
            label: 'Average Duration',
            value: statistics.value.averageDuration ? `${statistics.value.averageDuration.toFixed(1)} min` : '-',
          },
        ],
      ],
    },
    {
      group: 'Costs',
      rows: [
        [
          {
            label: 'Total spent this month',
            value: (
              statistics.value.ridesPartnerCost +
              statistics.value.ridesPartnerCredits +
              statistics.value.ridesCustomChargeCost +
              statistics.value.ridesCustomChargeCredits
            ).toFixed(2),
          },
        ],
        [
          {
            label: 'Rides Partner Cost',
            value: statistics.value.ridesPartnerCost.toFixed(2),
          },
          {
            label: 'Rides Partner Credits',
            value: statistics.value.ridesPartnerCredits.toFixed(2),
          },
        ],
        [
          {
            label: 'Rides Custom Charge Cost',
            value: statistics.value.ridesCustomChargeCost.toFixed(2),
          },
          {
            label: 'Rides Custom Charge Credits',
            value: statistics.value.ridesCustomChargeCredits.toFixed(2),
          },
        ],
        [
          {
            label: 'Average Ride Partner Cost',
            value: statistics.value.averageRidePartnerCost ? statistics.value.averageRidePartnerCost.toFixed(2) : '-',
          },
          {
            label: 'Average Ride Partner Credits',
            value: statistics.value.averageRidePartnerCredits ? statistics.value.averageRidePartnerCredits.toFixed(2) : '-',
          },
        ],
      ],
    },
    ...(period.value === 'currentMonth'
      ? [
          {
            group: `Year to Date (from ${moment.utc().startOf('year').format('MMM Do')} to ${moment.utc().format('MMM Do')})`,
            rows: [
              [
                {
                  label: 'Total spent this year',
                  value: (
                    statistics.value.ytdRidesPartnerCost +
                    statistics.value.ytdRidesPartnerCredits +
                    statistics.value.ytdRidesCustomChargeCost +
                    statistics.value.ytdRidesCustomChargeCredits
                  ).toFixed(2),
                },
              ],
            ],
          },
        ]
      : []),
  ];
});

const loadReport = async () => {
  status.isLoading = true;
  statistics.value = await fetchReport({ shouldUpdateCache: false });
  status.isLoading = false;
};

const refreshReport = async () => {
  status.isLoading = true;
  statistics.value = await fetchReport({ shouldUpdateCache: true });
  status.isLoading = false;
};

const fetchReport = async ({ shouldUpdateCache }: { shouldUpdateCache: boolean }) => {
  return fetchPartnerRideHistoryReport(
    {
      partnerCodeName: partnerCode.value,
      fromDate: fromDate.value,
      toDate: toDate.value,
    },
    shouldUpdateCache,
  );
};

watch(period, () => loadReport());
</script>
